import { createStore, type StoreApi } from 'zustand/vanilla';
import type { Geometry } from '../doc/schema';
import type { Rect, Size } from '../doc/geometry';
import type { Camera } from './viewport/camera';

export type Tool = 'select' | 'pan' | 'note' | 'title' | 'richtext' | 'table' | 'todo' | 'kanban' | 'shape' | 'frame' | 'draw' | 'exportArea';

/**
 * Per-pane UI state. Everything that is *not* document content: camera, selection, tool,
 * in-progress drag geometry. A second pane (split view) simply gets a second store.
 */
export interface PaneState {
  camera: Camera;
  /** Zoom after the user stopped zooming; image LOD switches on this, not on every frame. */
  settledZoom: number;
  size: Size;
  tool: Tool;
  selection: ReadonlySet<string>;
  editingId: string | null;
  /** Geometry overrides while dragging/resizing, committed to the doc on release. */
  transient: Readonly<Record<string, Partial<Geometry>>>;
  marquee: Rect | null;
  /** Rectangle being dragged out with a creation tool (shape). */
  creating: Rect | null;
  /** Snap guide lines (world coords) shown during a drag. */
  snapGuides: { x: number | null; y: number | null };
  spaceHeld: boolean;
  dragOver: boolean;

  setCamera(c: Camera): void;
  setSize(s: Size): void;
  setTool(t: Tool): void;
  select(ids: Iterable<string>): void;
  addToSelection(ids: Iterable<string>): void;
  toggleSelected(id: string): void;
  clearSelection(): void;
  setEditing(id: string | null): void;
  setTransient(t: Record<string, Partial<Geometry>>): void;
  clearTransient(): void;
  setMarquee(r: Rect | null): void;
  setCreating(r: Rect | null): void;
  setSnapGuides(g: { x: number | null; y: number | null }): void;
  setSpaceHeld(v: boolean): void;
  setDragOver(v: boolean): void;
}

export type PaneStore = StoreApi<PaneState>;

const EMPTY_SELECTION: ReadonlySet<string> = new Set();
const EMPTY_TRANSIENT: Readonly<Record<string, Partial<Geometry>>> = Object.freeze({});
const NO_GUIDES = Object.freeze({ x: null, y: null }) as { x: number | null; y: number | null };

export function createPaneStore(initialCamera: Camera = { x: 0, y: 0, zoom: 1 }): PaneStore {
  let settleTimer: ReturnType<typeof setTimeout> | undefined;

  return createStore<PaneState>((set, get) => ({
    camera: initialCamera,
    settledZoom: initialCamera.zoom,
    size: { w: 0, h: 0 },
    tool: 'select',
    selection: EMPTY_SELECTION,
    editingId: null,
    transient: EMPTY_TRANSIENT,
    marquee: null,
    creating: null,
    snapGuides: NO_GUIDES,
    spaceHeld: false,
    dragOver: false,

    setCamera(camera) {
      const prev = get().camera;
      set({ camera });
      if (camera.zoom !== prev.zoom) {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(() => set({ settledZoom: get().camera.zoom }), 150);
      }
    },
    setSize(size) {
      const s = get().size;
      if (s.w !== size.w || s.h !== size.h) set({ size });
    },
    setTool(tool) {
      if (get().tool !== tool) set({ tool, creating: null });
    },
    select(ids) {
      const next = new Set(ids);
      const cur = get().selection;
      if (next.size === cur.size && [...next].every((id) => cur.has(id))) return;
      set({ selection: next.size ? next : EMPTY_SELECTION });
    },
    addToSelection(ids) {
      const next = new Set(get().selection);
      for (const id of ids) next.add(id);
      set({ selection: next });
    },
    toggleSelected(id) {
      const next = new Set(get().selection);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      set({ selection: next.size ? next : EMPTY_SELECTION });
    },
    clearSelection() {
      if (get().selection.size) set({ selection: EMPTY_SELECTION });
    },
    setEditing(editingId) {
      if (get().editingId !== editingId) set({ editingId });
    },
    setTransient(transient) {
      set({ transient });
    },
    clearTransient() {
      if (get().transient !== EMPTY_TRANSIENT) set({ transient: EMPTY_TRANSIENT });
    },
    setMarquee(marquee) {
      set({ marquee });
    },
    setCreating(creating) {
      set({ creating });
    },
    setSnapGuides(g) {
      const cur = get().snapGuides;
      if (cur.x === g.x && cur.y === g.y) return;
      set({ snapGuides: g.x === null && g.y === null ? NO_GUIDES : g });
    },
    setSpaceHeld(spaceHeld) {
      if (get().spaceHeld !== spaceHeld) set({ spaceHeld });
    },
    setDragOver(dragOver) {
      if (get().dragOver !== dragOver) set({ dragOver });
    },
  }));
}
